import { useCallback, useEffect, useRef, useState } from "react";
import { ProductSearchItem } from "../types/productV3";
import {
  productSearchService,
  ProductSearchResponse,
} from "../services/productSearchService";

interface UseDiscoverProductsResult {
  allItems: ProductSearchItem[];
  isLoading: boolean;
  error: string | null;
  retry: () => void;
}

const DISCOVER_PRODUCTS_PAGE_SIZE = 1000;
const DISCOVER_PRODUCTS_FALLBACK_PAGE_SIZE = 100;

type RawProductSearchItem = Omit<ProductSearchItem, "performance"> & {
  performance?: ProductSearchItem["performance"];
};

function getProductKey(item: RawProductSearchItem): string {
  return `${item.productType}:${item.providerId}:${item.productId}`;
}

function shouldReplaceDedupedProduct(
  existing: RawProductSearchItem,
  candidate: RawProductSearchItem
): boolean {
  return existing.performance == null && candidate.performance != null;
}

function dedupeProducts(items: RawProductSearchItem[]): RawProductSearchItem[] {
  const byKey = new Map<string, RawProductSearchItem>();
  for (const item of items) {
    const key = getProductKey(item);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, item);
    } else if (shouldReplaceDedupedProduct(existing, item)) {
      byKey.set(key, item);
    }
  }
  return Array.from(byKey.values());
}

function normalizeProductSearchItem(item: RawProductSearchItem): ProductSearchItem {
  return {
    ...item,
    performance: item.performance ?? null,
    likeCount: item.likeCount ?? 0,
    likedByCurrentUser: item.likedByCurrentUser ?? false,
    contractRemarks: item.contractRemarks ?? [],
    restrictionFlags: item.restrictionFlags ?? [],
  };
}

function isPageSizeRejected(err: unknown): boolean {
  return Boolean(
    err &&
      typeof err === "object" &&
      "status" in err &&
      (err as { status?: number }).status === 400
  );
}

export function useDiscoverProducts(options?: { admin?: boolean }): UseDiscoverProductsResult {
  const useAdminSearch = options?.admin === true;
  const [allItems, setAllItems] = useState<ProductSearchItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [retryTrigger, setRetryTrigger] = useState(0);

  const searchFn = useAdminSearch
    ? (params: { page?: number; pageSize: number }, signal: AbortSignal) =>
        productSearchService.searchAdminProducts(params, signal)
    : (params: { page?: number; pageSize: number }, signal: AbortSignal) =>
        productSearchService.searchProducts(params, signal);

  const fetchProducts = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);
    setError(null);

    try {
      let pageSize = DISCOVER_PRODUCTS_PAGE_SIZE;
      let firstPage: ProductSearchResponse;

      try {
        firstPage = await searchFn(
          { pageSize },
          controller.signal
        );
      } catch (err) {
        if (!isPageSizeRejected(err)) throw err;
        pageSize = DISCOVER_PRODUCTS_FALLBACK_PAGE_SIZE;
        firstPage = await searchFn(
          { pageSize },
          controller.signal
        );
      }

      if (fetchId !== fetchIdRef.current) return;

      let items = firstPage.items;

      if (firstPage.totalPages > 1) {
        const remainingPromises: Promise<ProductSearchResponse>[] = [];
        for (let p = 2; p <= firstPage.totalPages; p++) {
          remainingPromises.push(
            searchFn(
              {
                page: p,
                pageSize,
              },
              controller.signal
            )
          );
        }
        const remainingPages = await Promise.all(remainingPromises);

        if (fetchId !== fetchIdRef.current) return;

        for (const page of remainingPages) {
          items = items.concat(page.items);
        }
      }

      setAllItems(dedupeProducts(items).map(normalizeProductSearchItem));
      setIsLoading(false);
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;

      const isAbort =
        (err instanceof Error && err.name === "AbortError") ||
        (err &&
          typeof err === "object" &&
          "status" in err &&
          (err as { status: number }).status === 408);
      if (isAbort) return;

      setError("Failed to load products. Please try again.");
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useAdminSearch]);

  useEffect(() => {
    fetchProducts();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchProducts, retryTrigger]);

  const retry = useCallback(() => {
    setRetryTrigger((prev) => prev + 1);
  }, []);

  return { allItems, isLoading, error, retry };
}
